<template>
  <v-container
    fluid
    class="pa-0 rounded-lg"
  >
    <v-card
      flat
      width="auto"
      class="rounded-lg"
    >
      <v-card-text class="pa-0">
        <v-sheet
          color="secondaryBackground"
          style="position: sticky; top: 55px; z-index: 2"
        >
          <v-tabs
            v-model="activeTab.notifications"
            grow
            color="review"
            show-arrows
            height="48"
            background-color="transparent"
          >
            <v-tabs-slider color="review" />
            <v-tab style="opacity: 1 !important">
              <v-icon
                small
                left
              >
                mdi-bell-ring-outline
              </v-icon>
              <span class="text-caption font-weight-bold">Unread</span>
              <v-badge
                inline
                class="ml-1"
                color="error"
                :value="unreadNotifications.length > 0"
                :content="unreadNotifications.length"
              />
            </v-tab>
            <v-tab style="opacity: 1 !important">
              <v-icon
                small
                left
              >
                mdi-bell-outline
              </v-icon>
              <span class="text-caption font-weight-bold">All</span>
              <v-badge
                inline
                class="ml-1"
                color="review"
                :value="allNotifications.length > 0"
                :content="allNotifications.length"
              />
            </v-tab>
            <v-tab style="opacity: 1 !important">
              <v-icon
                small
                left
              >
                mdi-send-outline
              </v-icon>
              <span class="text-caption font-weight-bold">Sent</span>
              <v-badge
                inline
                class="ml-1"
                color="success"
                :value="sentNotifications.length > 0"
                :content="sentNotifications.length"
              />
            </v-tab>
          </v-tabs>
          <v-divider />
        </v-sheet>

        <div class="pa-3">
          <div
            v-if="notifications.length === 0"
            class="d-flex flex-column align-center justify-center py-10 text--secondary"
          >
            <v-icon
              large
              color="background4"
              class="mb-2"
            >
              mdi-bell-outline
            </v-icon>
            <span class="text-body-2">No{{
              activeTab.notifications === 0
                ? " unread"
                : activeTab.notifications === 2
                  ? " sent"
                  : ""
            }} notifications</span>
          </div>

          <template v-else>
            <div
              v-if="activeTab.notifications !== 2 && notifications.some(n => !n.viewed) && notifications.length > 1"
              class="d-flex justify-end mb-2"
            >
              <v-btn
                small
                text
                color="review"
                :loading="loading.submit"
                @click="traverse('markAllComplete')"
              >
                <v-icon
                  small
                  left
                >
                  mdi-check-all
                </v-icon>
                Mark all as read
              </v-btn>
            </div>

            <div
              class="d-flex flex-column"
              style="gap: 8px"
            >
              <v-sheet
                v-for="notification in notifications"
                :key="notification.id"
                class="rounded-lg pa-3"
                :color="
                  !notification.viewed && activeTab.notifications !== 2
                    ? `${$vuetify.theme.currentTheme.review}14`
                    : 'transparent'
                "
                :style="{
                  border: `1px solid ${$vuetify.theme.currentTheme.background3}`,
                  cursor: 'pointer',
                }"
                @click="traverse('notification-view', notification)"
              >
                <!-- Meta: type + unread dot · timestamp -->
                <div class="d-flex align-center justify-space-between mb-2">
                  <div
                    class="d-flex align-center"
                    style="gap: 6px; min-width: 0"
                  >
                    <v-badge
                      v-if="!notification.viewed && activeTab.notifications !== 2"
                      dot
                      inline
                      :color="$vuetify.theme.currentTheme.review"
                    />
                    <v-icon
                      size="15"
                      color="secondaryIcon"
                    >
                      {{ notification.icon ?? "mdi-bell" }}
                    </v-icon>
                    <span
                      class="text--secondary font-weight-bold text-caption text-uppercase text-truncate"
                      style="letter-spacing: 0.4px"
                    >{{ notification.type }}</span>
                  </div>
                  <span class="text--secondary text-caption text-no-wrap">
                    {{ notification.created | moment("MMM D · h:mm A") }}
                  </span>
                </div>

                <!-- Record name (title) -->
                <div
                  class="text-body-2 font-weight-black mb-1"
                  style="line-height: 1.3"
                >
                  {{ notification.name }}
                </div>

                <!-- Notes / description -->
                <div
                  v-if="notification.notes"
                  class="text-body-2 text--primary"
                  style="line-height: 1.5; word-break: break-word"
                  v-html="(notification.notes || '').replace(/\n/g, '<br>')"
                />

                <v-divider class="my-3" />

                <!-- Footer: meta + actions -->
                <div
                  class="d-flex align-end justify-space-between"
                  style="gap: 8px"
                >
                  <div
                    class="d-flex flex-column"
                    style="min-width: 0"
                  >
                    <span class="text-capitalize text-caption text--secondary text-truncate">
                      {{
                        activeTab.notifications === 2
                          ? `Sent to ${notification.assignedTo}`
                          : `From ${notification.createdBy}`
                      }}
                    </span>
                    <div
                      v-if="activeTab.notifications === 2 && notification.viewed"
                      class="d-flex align-center mt-1"
                    >
                      <v-icon
                        :color="$vuetify.theme.currentTheme.success"
                        size="13"
                        class="mr-1"
                      >
                        mdi-check-all
                      </v-icon>
                      <span
                        class="text-caption"
                        :style="`color: ${$vuetify.theme.currentTheme.success}`"
                      >Read {{ notification.updated | moment("MMM D · h:mm A") }}</span>
                    </div>
                  </div>
                  <div
                    class="d-flex align-center"
                    @click.stop
                  >
                    <v-tooltip
                      v-if="!notification.viewed && activeTab.notifications !== 2"
                      bottom
                    >
                      <template #activator="{ on, attrs }">
                        <v-btn
                          plain
                          x-small
                          color="review"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="traverse('notification-mark-read', notification)"
                        >
                          <v-icon size="14">mdi-check</v-icon>
                        </v-btn>
                      </template>
                      <span>Mark as read</span>
                    </v-tooltip>
                    <v-btn
                      plain
                      x-small
                      color="review"
                      @click.stop="copyDetails(notification)"
                    >
                      <v-icon
                        size="14"
                        left
                      >
                        mdi-content-copy
                      </v-icon>
                      Copy
                    </v-btn>
                    <v-edit-dialog
                      large
                      persistent
                      cancel-text="Cancel"
                      save-text="Send"
                      @open="traverse('reply-show', notification)"
                      @save="traverse('reply-submit')"
                    >
                      <v-btn
                        plain
                        x-small
                        color="review"
                      >
                        <v-icon
                          size="14"
                          left
                        >
                          mdi-message-reply-text
                        </v-icon>
                        Reply
                      </v-btn>
                      <template #input>
                        <v-text-field
                          v-model.trim="payload.reply.msg"
                          label="Reply"
                          placeholder="Enter reply…"
                          persistent-placeholder
                          clearable
                          autofocus
                          @click:clear="payload.reply.msg = ''"
                        />
                      </template>
                    </v-edit-dialog>
                  </div>
                </div>
              </v-sheet>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>

    <!-- Success Snackbar -->
    <v-snackbar
      :value="snackbar.active"
      :color="snackbar.color"
      timeout="2500"
      centered
      rounded="lg"
      transition="fade-transition"
    >
      <v-layout
        align-center
        justify-space-around
      >
        <v-icon
          class="pr-3"
          dark
        >
          {{ snackbar.icon }}
        </v-icon>
        <v-layout column>
          <div class="text-subtitle-1 font-weight-bold">
            {{ snackbar.msg }}
          </div>
        </v-layout>
      </v-layout>
    </v-snackbar>
  </v-container>
</template>

<script>
// Libraries
import { mapActions, mapGetters } from "vuex";
import {
  // Auth
  auth,
  // Firestore
  firestore,
  collection,
  onSnapshot,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  or,
  writeBatch,
  limit,
  // Functions
  functions,
  httpsCallable,
} from "@/firebase/init";
import moment from "moment";
// Components

export default {
  name: "Notifications",
  data: () => ({
    loading: {
      submit: false,
    },
    activeTab: {
      notifications: 0,
    },
    response: {
      notifications: [],
    },
    payload: {
      reply: {
        notification: null,
        msg: "",
      },
    },
    snackbar: {
      msg: "",
      icon: "",
      color: "",
      active: "",
    },
  }),
  async created() {
    this.setup();
  },
  methods: {
    ...mapActions(["_rightDrawer"]),
    /* API */
    // GET
    async getNotifications() {
      this.response.notifications = [];
      const notificationsRef = collection(firestore, "notifications");
      const relatedUser = or(
        where("assignedTo.uid", "==", this.getUser.uid),
        where("createdBy.uid", "==", this.getUser.uid)
      );

      const q = query(notificationsRef, relatedUser);
      onSnapshot(q, (response) => {
        if (!response.empty) {
          this.response.notifications = response.docs
            .map((notification) => {
              return {
                id: notification.id,
                viewed: notification.data().viewed,
                createdBy: `${notification.data().createdBy?.name?.first ?? ""} ${notification.data().createdBy?.name?.last ?? ""}`.trim() || "Unknown",
                assignedTo: `${notification.data().assignedTo?.name?.first ?? ""} ${notification.data().assignedTo?.name?.last ?? ""}`.trim() || "Unknown",
                created: moment(
                  notification.data().created?.seconds * 1000
                ).toDate(),
                updated: moment(
                  notification.data().updated?.seconds * 1000
                ).toDate(),
                relatedTo: notification.data().relatedTo,
                notes: notification.data().notes,
                createdByUID: notification.data().createdBy.uid,
                assignedToUID: notification.data().assignedTo.uid,
                name: notification.data().relatedTo.name,
                type: notification.data().relatedTo.type,
                icon: notification.data().relatedTo.icon ?? null,
                source: notification.data().source ?? null,
              };
            })
            .sort((a, b) => (a.created > b.created ? -1 : 1));
        } else {
          this.response.notifications = [];
        }
      });
    },
    // POST
    async reply() {
      const batch = writeBatch(firestore);
      this.loading.submit = true;
      let slackActive = false;

      const configDoc = doc(firestore, "config", "config");
      const configResponse = await getDoc(configDoc);
      if (configResponse.exists) {
        slackActive = configResponse.data()?.slack || false;
      }

      let slackIntegrationConfigured = false;
      if (slackActive) {
        const integrationsRef = collection(firestore, "integrations"),
          related = where("platform", "==", "slack");
        const q = query(integrationsRef, related, limit(1));
        const response = await getDocs(q);
        slackIntegrationConfigured = !response.empty;
      }

      if (this.payload.reply.msg.length > 0) {
        const userDoc = doc(
          firestore,
          "users",
          this.payload.reply.notification.createdByUID
        );
        const userResponse = await getDoc(userDoc);

        let ledgerEntries = [
          // Admin
          "task",
          "projects",
          // Sourcing
          "vendorOrder-productCategory",
          "vendorOrder-invoice",
          // Manufacturing
          "rawMaterialOrders",
          "productWorkOrder",
          // Sales
          "salesOrder",
          // Support
          "budtenderTrainings",
          "storeEvents",
          "supportTickets",
        ];

        batch.set(
          doc(collection(firestore, "notifications")),
          {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            assignedTo: {
              name: {
                first: this.payload.reply.notification.createdBy.split(" ")[0],
                last: this.payload.reply.notification.createdBy.split(" ")[1],
              },
              uid: this.payload.reply.notification.createdByUID,
            },
            createdBy: {
              name: this.getUser.name,
              uid: this.getUser.uid,
            },
            lastUpdated: {
              name: this.getUser.name,
              uid: this.getUser.uid,
            },
            notes: this.payload.reply.msg,
            relatedTo: this.payload.reply.notification.relatedTo,
            viewed: false,
          },
          { merge: true }
        );
        if (
          ledgerEntries.includes(
            this.payload.reply.notification.relatedTo.resource
          )
        ) {
          batch.set(
            doc(collection(firestore, "ledger")),
            {
              created: serverTimestamp(),
              createdBy: {
                name: this.getUser.name,
                uid: this.getUser.uid,
              },
              date: serverTimestamp(),
              relatedID: this.payload.reply.notification.relatedTo.id,
              productWorkOrderID:
                this.payload.reply.notification.relatedTo.resource ===
                "productWorkOrder"
                  ? this.payload.reply.notification.relatedTo.id
                  : null,
              type: "notification",
              assignedTo: {
                name: {
                  first:
                    this.payload.reply.notification.createdBy.split(" ")[0],
                  last: this.payload.reply.notification.createdBy.split(" ")[1],
                },
                uid: this.payload.reply.notification.createdByUID,
              },
              notes: this.payload.reply.msg,
              action: `<span class="text-capitalize">${
                this.getUser.name.first
              } ${
                this.getUser.name.last
              }</span> sent a notification to <span class="text-capitalize">${
                this.payload.reply.notification.createdBy.split(" ")[0]
              } ${
                this.payload.reply.notification.createdBy.split(" ")[1]
              }</span> | <span class="text-capitalize">notes</span>: "${
                this.payload.reply.msg
              }"`,
              user: {
                name: this.getUser.name,
                uid: this.getUser.uid,
              },
            },
            { merge: true }
          );
        }

        if (slackIntegrationConfigured) {
          await this.sendSlackMessage({
            email: userResponse.data().email,
            text: `:bell: ${this.$options.filters.capitalize(
              this.getUser.name.first
            )} ${this.$options.filters.capitalize(
              this.getUser.name.last
            )} has sent you a new notification\nRecord: ${this.$options.filters.capitalize(
              this.payload.reply.notification.relatedTo.name
            )} | ${this.$options.filters.capitalize(
              this.payload.reply.notification.relatedTo.type
            )}\nNotes: "${this.payload.reply.msg}"`,
          });
        }

        await batch.commit();
      } else {
        this.snackbar.active = true;
        this.snackbar = {
          active: true,
          msg: "A reply message must be set!",
          color: "red",
          icon: "mdi-warning",
        };
      }
      this.loading.submit = false;
    },
    async sendSlackMessage(payload) {
      const slackMessage = httpsCallable(functions, "slackMessage");
      await slackMessage(payload);
    },
    async markAllAsComplete() {
      this.loading.submit = true;
      const batch = writeBatch(firestore);

      this.notifications.forEach((notification) => {
        const notificationDoc = doc(
          firestore,
          "notifications",
          notification.id
        );
        batch.set(
          notificationDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: this.getUser.name,
              uid: this.getUser.uid,
            },
            viewed: true,
          },
          { merge: true }
        );
      });
      await batch.commit();

      this.loading.submit = false;
    },
    /* Main */
    async setup() {
      if (auth.currentUser) {
        await this.getNotifications();
      }
    },
    async traverse(action, params) {
      let link;
      switch (action) {
        case "markAllComplete":
          this.markAllAsComplete();
          break;
        case "notification-mark-read":
          await updateDoc(doc(firestore, "notifications", params.id), {
            updated: serverTimestamp(),
            lastUpdated: {
              name: this.getUser.name,
              uid: this.getUser.uid,
            },
            viewed: true,
          });
          break;
        case "notification-view":
          if (!params.viewed && this.activeTab.notifications !== 2) {
            await updateDoc(
              doc(firestore, "notifications", params.id),
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: this.getUser.name,
                  uid: this.getUser.uid,
                },
                viewed: true,
              },
              { merge: true }
            );
          }
          if (params.relatedTo.path) {
            if (this.$router.currentRoute.path === params.relatedTo.path) {
              this.$router.push(params.relatedTo.path);
              this.$router.go();
            } else {
              this.$router.push(params.relatedTo.path);
            }
          } else if (
            this.$router.currentRoute.name ===
            `${params.relatedTo.resource}-view`
          ) {
            let routerParams = {
              id: params.relatedTo.id,
            };

            if (params.relatedTo.locationID) {
              routerParams.locationID = params.relatedTo.locationID;
            }

            this.$router.push({
              name: `${params.relatedTo.resource}-view`,
              params: {
                ...routerParams,
              },
            });
            this.$router.go();
          } else {
            let routerParams = {
              id: params.relatedTo.id,
            };

            if (params.relatedTo.locationID) {
              routerParams.locationID = params.relatedTo.locationID;
            }
            this.$router.push({
              name: `${params.relatedTo.resource}-view`,
              params: {
                ...routerParams,
              },
            });
          }
          break;
        case "reply-show":
          this.resetReply();
          this.payload.reply.notification = params;
          break;
        case "reply-submit":
          this.reply();
          break;
      }
    },
    async copyDetails(notification) {
      let textToCopy = `${notification.notes} | ${notification.name}`;

      try {
        await navigator.clipboard.writeText(textToCopy);

        this.snackbar = {
          active: true,
          msg: "Details copied to clipboard!",
          icon: "mdi-check-circle",
          color: "success",
        };
      } catch (error) {
        console.error(error.message);
        this.snackbar = {
          active: true,
          msg: "Error copying details to clipboard!",
          icon: "mdi-alert-circle",
          color: "error",
        };
      }

      setTimeout(() => {
        this.snackbar = {
          active: false,
          msg: "",
          icon: "",
          color: "",
        };
      }, 2500);
    },
    /* Misc */
    resetReply() {
      this.payload.reply = {
        notification: null,
        msg: "",
      };
    },
  },
  computed: {
    ...mapGetters([
      // App
      "getAppName",
      "getUser",
      "getRightDrawer",
    ]),
    rightDrawer: {
      get: function () {
        return this.getRightDrawer;
      },
      set: function (value) {
        this._rightDrawer({
          ...this.getRightDrawer,
          active: value,
        });
      },
    },
    // Notifications
    unreadNotifications() {
      return this.response.notifications.filter(
        (notification) =>
          !notification.viewed &&
          notification.assignedToUID === this.getUser.uid &&
          notification.source !== "watcher"
      );
    },
    allNotifications() {
      return this.response.notifications.filter(
        (item) => item.assignedToUID === this.getUser.uid && item.source !== "watcher"
      );
    },
    sentNotifications() {
      return this.response.notifications.filter(
        (item) => item.createdByUID === this.getUser.uid && item.source !== "watcher"
      );
    },
    notifications() {
      switch (this.activeTab.notifications) {
        case 0:
          return this.unreadNotifications;
          break;
        case 1:
          return this.allNotifications;
          break;
        case 2:
          return this.sentNotifications;
          break;
        default:
          return [];
          break;
      }
    },
  },
};
</script>

<style></style>
